import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Calendar,
  DollarSign,
  TrendingUp,
  Users,
  Heart,
  Edit2,
  Camera,
  Upload,
  Save,
  X,
  Loader,
  ChevronRight,
  Award,
  Clock,
  CheckCircle,
  AlertCircle,
  Image as ImageIcon,
  VectorSquareIcon,
  Calculator
} from 'lucide-react';
import { format } from 'date-fns';
import './userDashView.css';
import { AuthContext } from '../../contexts/AuthContext';
import NextOfKin from './NextOfKinSection';
import DependentsManager from '../../components/DependentsManager';
import { Modal, Button, Form } from 'react-bootstrap';
import UserContributions from './UserContributions';

const UserDashView = () => {
  const { user: authUser, token, updateUser } = useContext(AuthContext);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  // Image upload states
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [showUploadBox, setShowUploadBox] = useState(false);
  const fileInputRef = useRef(null);

  // Fetch user profile
  useEffect(() => {
    fetchUserProfile();
  }, [authUser?.id]);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      const userId = authUser?.id || 6; // Fallback to 6 for testing
      const response = await fetch(`https://aceswafund.org/welfare/welfare-system/get-profile?user_id=${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      const result = await response.json();

      if (result.status) {
        setUserData(result.data);
        setEditFormData({
          full_name: result.data.full_name,
          phone: result.data.phone,
          address: result.data.address || '',
          department: result.data.department || '',
          position: result.data.position || '',
          gender: result.data.gender || '',
          date_of_birth: result.data.date_of_birth || '',
          joined_at: result.data.joined_at || '',
          contribution_start_date: result.data.contribution_start_date || ''
        });
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Failed to fetch profile');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        setUploadError('Please select a valid image file (JPG, PNG, GIF, WEBP)');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setUploadError('File size must be less than 5MB');
        return;
      }

      setSelectedFile(file);
      setUploadError(null);

      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);

      // Auto-show upload box when file is selected
      setShowUploadBox(true);
    }
  };

  const handleUploadProfileImage = async () => {
    if (!selectedFile) {
      setUploadError('Please select an image first');
      return;
    }

    setUploadLoading(true);
    setUploadError(null);
    setUploadSuccess(false);

    try {
      const formData = new FormData();
      formData.append('profile_image', selectedFile);
      formData.append('user_id', authUser?.id || 6);

      const response = await fetch('https://aceswafund.org/welfare/welfare-system/update-profile-image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const result = await response.json();

      if (result.status) {
        setUploadSuccess(true);

        // Update user data with new image
        setUserData(prev => ({
          ...prev,
          profile_image_url: result.image_url,
          profile_image: result.data?.profile_image
        }));

        // Update auth context if needed
        if (updateUser) {
          updateUser({
            ...authUser,
            profile_image_url: result.profile_image_url
          });
        }

        // Reset upload states
        setSelectedFile(null);
        setPreviewImage(null);
        setShowUploadBox(false);

        // Auto-hide success message after 3 seconds
        setTimeout(() => setUploadSuccess(false), 3000);
      } else {
        setUploadError(result.message || 'Upload failed');
      }
    } catch (err) {
      setUploadError('Failed to upload image');
      console.error('Error:', err);
    } finally {
      setUploadLoading(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setUpdateLoading(true);
    setUpdateSuccess(false);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('user_id', authUser?.id || 6);

      // Append all form fields
      Object.keys(editFormData).forEach(key => {
        if (editFormData[key] !== null && editFormData[key] !== undefined) {
          formData.append(key, editFormData[key]);
        }
      });

      // Add this debug log to see what's being sent
      for (let pair of formData.entries()) {
        console.log(pair[0] + ': ' + pair[1]);
      }

      const response = await fetch('https://aceswafund.org/welfare/welfare-system/update-profile', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // REMOVE the Content-Type header - let browser set it
        },
        body: formData
      });

      const result = await response.json();
      console.log('Update response:', result);

      if (result.status) {
        setUpdateSuccess(true);
        setShowEditModal(false);

        // Check where the user data is in the response
        const updatedUserData = result.user || result.data;

        if (updatedUserData) {
          // Update user data with response
          setUserData(updatedUserData);

          // Update auth context with new user data AND new token
          if (updateUser) {
            updateUser({
              ...authUser,
              ...updatedUserData
            }, result.token);
          }

          // Update edit form data with new values
          setEditFormData({
            full_name: updatedUserData.full_name || '',
            phone: updatedUserData.phone || '',
            address: updatedUserData.address || '',
            department: updatedUserData.department || '',
            position: updatedUserData.position || '',
            gender: updatedUserData.gender || '',
            date_of_birth: updatedUserData.date_of_birth || '',
            joined_at: updatedUserData.joined_at || '',
            contribution_start_date: updatedUserData.contribution_start_date || ''
          });
        }

        setTimeout(() => setUpdateSuccess(false), 3000);
      } else {
        setError(result.message || 'Update failed');
      }
    } catch (err) {
      setError('Failed to update profile');
      console.log(err);
    } finally {
      setUpdateLoading(false);
    }
  };

  const cancelUpload = () => {
    setSelectedFile(null);
    setPreviewImage(null);
    setShowUploadBox(false);
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === '0000-00-00') return 'Not set';
    try {
      return format(new Date(dateString), 'MMMM dd, yyyy');
    } catch {
      return dateString;
    }
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const openEditModal = () => {
    setEditFormData({
      full_name: userData?.full_name || '',
      phone: userData?.phone || '',
      address: userData?.address || '',
      department: userData?.department || '',
      position: userData?.position || '',
      gender: userData?.gender || '',
      date_of_birth: userData?.date_of_birth || '',
      joined_at: userData?.joined_at || '',
      contribution_start_date: userData?.contribution_start_date || ''
    });
    setError(null);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setError(null);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
        <div className="text-center">
          <Loader className="spinner text-primary mb-3" size={48} />
          <p className="text-muted">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error && !userData) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger" role="alert">
          <AlertCircle className="me-2" size={20} />
          {error}
        </div>
      </div>
    );
  }

  const coverStyle = {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    height: '300px',
    position: 'relative',
    borderRadius: '1rem 1rem 0 0'
  };

  return (
    <div className="container-fluid px-4 py-4">
      {/* Success Toasts */}
      {updateSuccess && (
        <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1050 }}>
          <div className="alert alert-success alert-dismissible fade show d-flex align-items-center shadow" role="alert">
            <CheckCircle className="me-2" size={20} />
            <div>Profile updated successfully!</div>
            <button type="button" className="btn-close" onClick={() => setUpdateSuccess(false)}></button>
          </div>
        </div>
      )}

      {uploadSuccess && (
        <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1050 }}>
          <div className="alert alert-success alert-dismissible fade show d-flex align-items-center shadow" role="alert">
            <CheckCircle className="me-2" size={20} />
            <div>Profile image uploaded successfully!</div>
            <button type="button" className="btn-close" onClick={() => setUploadSuccess(false)}></button>
          </div>
        </div>
      )}

      {/* Cover Section */}
      <div className="card shadow-sm border-0 overflow-hidden mb-4">
        <div style={coverStyle} className="position-relative">
          {/* Cover Actions */}
          <div className="position-absolute bottom-0 end-0 p-4">
            <button
              className="btn btn-light btn-sm rounded-pill px-3"
              onClick={openEditModal}
            >
              <Edit2 size={16} className="me-1" />
              Edit Profile
            </button>
           
          </div>
        </div>

        {/* Profile Section */}
        <div className="px-4 pb-4 position-relative">
          <div className="row">
            <div className="col-auto" style={{ marginTop: '-75px' }}>
              <div className="position-relative">
                <div className="rounded-circle border border-4 border-white shadow-sm overflow-hidden bg-white"
                  style={{ width: '150px', height: '150px' }}>
                  {previewImage ? (
                    <img src={previewImage} alt="Preview" className="w-100 h-100 object-fit-cover" />
                  ) : userData?.profile_image_url && !userData.profile_image_url.includes('default') ? (
                    <img
                      src={userData.profile_image_url}
                      alt={userData.full_name}
                      className="w-100 h-100 object-fit-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = `<div class="d-flex justify-content-center align-items-center h-100 bg-primary bg-opacity-10">
                          <span class="display-5 fw-bold text-primary">${getInitials(userData?.full_name || 'User')}</span>
                        </div>`;
                      }}
                    />
                  ) : (
                    <div className="d-flex justify-content-center align-items-center h-100 bg-primary bg-opacity-10">
                      <span className="display-5 fw-bold text-primary">
                        {getInitials(userData?.full_name || 'User')}
                      </span>
                    </div>
                  )}
                </div>

                {/* Upload Button with Tooltip */}
                <div className="position-absolute bottom-0 end-0 mb-2 me-2">
                  <button
                    className="btn btn-primary rounded-circle p-2 shadow-sm border-0"
                    onClick={() => setShowUploadBox(!showUploadBox)}
                    title="Update profile picture"
                    style={{ width: '40px', height: '40px' }}
                  >
                    <Camera size={18} className="text-white" />
                  </button>
                </div>
              </div>

              {/* Profile Image Upload Box */}
              {showUploadBox && (
                <div className="mt-3 p-3 bg-light rounded-4 border" style={{ maxWidth: '300px' }}>
                  <div className="d-flex align-items-center mb-2">
                    <ImageIcon size={18} className="text-primary me-2" />
                    <h6 className="mb-0 fw-semibold">Update Profile Picture</h6>
                    <button
                      className="btn-close ms-auto"
                      onClick={() => setShowUploadBox(false)}
                      style={{ fontSize: '0.8rem' }}
                    ></button>
                  </div>

                  <p className="small text-muted mb-3">
                    Upload a new profile image. Supported formats: JPG, PNG, GIF, WEBP (Max 5MB)
                  </p>

                  <div className="mb-3">
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="d-none"
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                      onChange={handleFileChange}
                      id="profile-image-upload"
                    />
                    <label
                      htmlFor="profile-image-upload"
                      className="btn btn-outline-primary w-100 d-flex align-items-center justify-content-center gap-2"
                      style={{ cursor: 'pointer' }}
                    >
                      <Upload size={16} />
                      Choose Image
                    </label>
                  </div>

                  {previewImage && (
                    <div className="mb-3">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <img
                          src={previewImage}
                          alt="Preview"
                          style={{ width: '50px', height: '50px', borderRadius: '8px', objectFit: 'cover' }}
                        />
                        <div className="small text-muted">
                          {selectedFile?.name} ({(selectedFile?.size / 1024).toFixed(1)} KB)
                        </div>
                      </div>

                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-primary btn-sm flex-grow-1 d-flex align-items-center justify-content-center gap-2"
                          onClick={handleUploadProfileImage}
                          disabled={uploadLoading}
                        >
                          {uploadLoading ? (
                            <>
                              <Loader size={14} className="spinner" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload size={14} />
                              Upload Now
                            </>
                          )}
                        </button>
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          onClick={cancelUpload}
                          disabled={uploadLoading}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    </div>
                  )}

                  {uploadError && (
                    <div className="alert alert-danger py-2 small mb-0">
                      <AlertCircle size={14} className="me-1" />
                      {uploadError}
                    </div>
                  )}

                  <div className="text-center mt-2">
                    <small className="text-muted">
                      <Camera size={12} className="me-1" />
                      Click the camera icon anytime to update
                    </small>
                  </div>
                </div>
              )}
            </div>

            <div className="col">
              <div className="mt-4">
                <h2 className="mb-1">{userData?.full_name}</h2>
                <p className="text-muted mb-2">
                  <Mail size={14} className="me-1" /> {userData?.email} •
                  <Phone size={14} className="ms-2 me-1" /> {userData?.phone}
                </p>
                <div className="d-flex gap-3 flex-wrap">
                  <span className="badge bg-light text-dark rounded-pill px-3 py-2">
                    <Briefcase size={14} className="me-1" /> {userData?.position || 'No position'}
                  </span>
                  <span className="badge bg-light text-dark rounded-pill px-3 py-2">
                    <Award size={14} className="me-1" /> {userData?.user_category || 'N/A'} Category
                  </span>
                  <span className="badge bg-light text-dark rounded-pill px-3 py-2">
                    <Calendar size={14} className="me-1" /> Joined {formatDate(userData?.joined_at)}
                  </span>
                    <span size={16} className={`badge p-2 mt-2 mt-md-0 text-white bg-success`} >  v2.4.0</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Profile Modal */}
      <Modal
        show={showEditModal}
        onHide={closeEditModal}
        size="lg"
        centered
        backdrop="static"
      >
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>
            <Edit2 size={20} className="me-2 text-primary" />
            Edit Profile
          </Modal.Title>
        </Modal.Header>

        <Form onSubmit={handleUpdateProfile}>
          <Modal.Body>
            {error && (
              <div className="alert alert-danger mb-4">
                <AlertCircle size={18} className="me-2" />
                {error}
              </div>
            )}

            <div className="row g-4">
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label className="fw-semibold">Full Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="full_name"
                    value={editFormData.full_name || ''}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter full name"
                  />
                </Form.Group>
              </div>

              <div className="col-md-6">
                <Form.Group>
                  <Form.Label className="fw-semibold">Phone</Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone"
                    value={editFormData.phone || ''}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter phone number"
                  />
                </Form.Group>
              </div>

              <div className="col-12">
                <Form.Group>
                  <Form.Label className="fw-semibold">Address</Form.Label>
                  <Form.Control
                    type="text"
                    name="address"
                    value={editFormData.address || ''}
                    onChange={handleInputChange}
                    placeholder="Enter address"
                  />
                </Form.Group>
              </div>

              <div className="col-md-6">
                <Form.Group>
                  <Form.Label className="fw-semibold">Department</Form.Label>
                  <Form.Control
                    type="text"
                    name="department"
                    value={editFormData.department || ''}
                    onChange={handleInputChange}
                    placeholder="Enter department"
                  />
                </Form.Group>
              </div>

              <div className="col-md-6">
                <Form.Group>
                  <Form.Label className="fw-semibold">Position</Form.Label>
                  <Form.Control
                    type="text"
                    name="position"
                    value={editFormData.position || ''}
                    onChange={handleInputChange}
                    placeholder="Enter position"
                  />
                </Form.Group>
              </div>

              <div className="col-md-4">
                <Form.Group>
                  <Form.Label className="fw-semibold">Gender</Form.Label>
                  <Form.Select
                    name="gender"
                    value={editFormData.gender || ''}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </Form.Select>
                </Form.Group>
              </div>

              <div className="col-md-4">
                <Form.Group>
                  <Form.Label className="fw-semibold">Date of Birth</Form.Label>
                  <Form.Control
                    type="date"
                    name="date_of_birth"
                    value={editFormData.date_of_birth || ''}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </div>

              <div className="col-md-4">
                <Form.Group>
                  <Form.Label className="fw-semibold">Joined Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="joined_at"
                    value={editFormData.joined_at || ''}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </div>

              <div className="col-12">
                <Form.Group>
                  <Form.Label className="fw-semibold">Contribution Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="contribution_start_date"
                    value={editFormData.contribution_start_date || ''}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </div>
            </div>
          </Modal.Body>

          <Modal.Footer className="bg-light">
            <Button variant="secondary" onClick={closeEditModal} disabled={updateLoading}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={updateLoading}
              className="px-4"
            >
              {updateLoading ? (
                <>
                  <Loader size={16} className="spinner me-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={16} className="me-2" />
                  Save Changes
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Main Content Tabs */}
      <div className="row g-4">
        <div className="col-12">
          <ul className="nav nav-tabs border-0">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'profile' ? 'active bg-white' : 'text-muted'}`}
                onClick={() => setActiveTab('profile')}
              >
                <User size={18} className="me-2" />
                Profile Information
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'nextofkin' ? 'active bg-white' : 'text-muted'}`}
                onClick={() => setActiveTab('nextofkin')}
              >
                <Heart size={18} className="me-2" />
                Next of Kin
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'dependents' ? 'active bg-white' : 'text-muted'}`}
                onClick={() => setActiveTab('dependents')}
              >
                <Users size={18} className="me-2" />
                Dependents
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'contributions' ? 'active bg-white' : 'text-muted'}`}
                onClick={() => setActiveTab('contributions')}
              >
                <DollarSign size={18} className="me-2" />
                Contributions
              </button>
            </li>
          </ul>
        </div>

        <div className="col-12">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="card shadow-sm border-0">
              <div className="card-body p-4">
                <div className="row g-4">
                  <div className="col-md-6">
                    <div className="d-flex align-items-center mb-3">
                      <div className="bg-light rounded-circle p-2 me-3">
                        <User size={20} className="text-primary" />
                      </div>
                      <div>
                        <small className="text-muted d-block">Full Name</small>
                        <span className="fw-medium">{userData?.full_name}</span>
                      </div>
                    </div>
                    <div className="d-flex align-items-center mb-3">
                      <div className="bg-light rounded-circle p-2 me-3">
                        <Mail size={20} className="text-primary" />
                      </div>
                      <div>
                        <small className="text-muted d-block">Email</small>
                        <span className="fw-medium">{userData?.email}</span>
                      </div>
                    </div>
                    <div className="d-flex align-items-center mb-3">
                      <div className="bg-light rounded-circle p-2 me-3">
                        <Phone size={20} className="text-primary" />
                      </div>
                      <div>
                        <small className="text-muted d-block">Phone</small>
                        <span className="fw-medium">{userData?.phone}</span>
                      </div>
                    </div>
                    <div className="d-flex align-items-center">
                      <div className="bg-light rounded-circle p-2 me-3">
                        <MapPin size={20} className="text-primary" />
                      </div>
                      <div>
                        <small className="text-muted d-block">Address</small>
                        <span className="fw-medium">{userData?.address || 'Not provided'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="d-flex align-items-center mb-3">
                      <div className="bg-light rounded-circle p-2 me-3">
                        <Briefcase size={20} className="text-primary" />
                      </div>
                      <div>
                        <small className="text-muted d-block">Department</small>
                        <span className="fw-medium">{userData?.department || 'Not assigned'}</span>
                      </div>
                    </div>
                    <div className="d-flex align-items-center mb-3">
                      <div className="bg-light rounded-circle p-2 me-3">
                        <Award size={20} className="text-primary" />
                      </div>
                      <div>
                        <small className="text-muted d-block">Position</small>
                        <span className="fw-medium">{userData?.position || 'Not assigned'}</span>
                      </div>
                    </div>
                    <div className="d-flex align-items-center mb-3">
                      <div className="bg-light rounded-circle p-2 me-3">
                        <Calendar size={20} className="text-primary" />
                      </div>
                      <div>
                        <small className="text-muted d-block">Date of Birth</small>
                        <span className="fw-medium">{formatDate(userData?.date_of_birth)}</span>
                      </div>
                    </div>
                    <div className="d-flex align-items-center">
                      <div className="bg-light rounded-circle p-2 me-3">
                        <Clock size={20} className="text-primary" />
                      </div>
                      <div>
                        <small className="text-muted d-block">Joined Welfare</small>
                        <span className="fw-medium">{formatDate(userData?.joined_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Next of Kin Tab */}
          {activeTab === 'nextofkin' && (
            <div className="card shadow-sm border-0">
              <NextOfKin />
            </div>
          )}

          {/* Dependents Tab */}
          {activeTab === 'dependents' && (
            <div className="card shadow-sm border-0">
              <DependentsManager />
            </div>
          )}

          {/* Contributions Tab */}
          {activeTab === 'contributions' && (
            <div className="card shadow-sm border-0">
              <div className="card-body p-4">
                <div className="row g-4 mb-4">
                  <div className="col-md-4">
                    <div className="bg-light rounded-4 p-4">
                      <div className="d-flex align-items-center mb-3">
                        <div className="bg-primary bg-opacity-10 rounded-circle p-3 me-3">
                          <DollarSign size={24} className="text-primary" />
                        </div>
                        <div>
                          <small className="text-muted d-block">Total Contributions</small>
                          <h3 className="mb-0">{userData?.contribution_stats?.total_contributions || 0}</h3>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="bg-light rounded-4 p-4">
                      <div className="d-flex align-items-center mb-3">
                        <div className="bg-success bg-opacity-10 rounded-circle p-3 me-3">
                          <TrendingUp size={24} className="text-success" />
                        </div>
                        <div>
                          <small className="text-muted d-block">Total Amount</small>
                          <h3 className="mb-0">GHS {userData?.contribution_stats?.total_amount?.toFixed(2) || '0.00'}</h3>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="bg-light rounded-4 p-4">
                      <div className="d-flex align-items-center mb-3">
                        <div className="bg-info bg-opacity-10 rounded-circle p-3 me-3">
                          <Award size={24} className="text-info" />
                        </div>
                        <div>
                          <small className="text-muted d-block">Category</small>
                          <h3 className="mb-0">{userData?.contribution_category || 'N/A'}</h3>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="text-center py-4">
                  <UserContributions />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDashView;